'use client';

import { useState, useMemo } from 'react';
import { Card, CardBody, Button, Slider, Input, Divider, Avatar, Alert, Switch, Tooltip } from '@heroui/react';
import { Icon } from '@iconify/react';
import type { LiquidityPosition } from '@/hooks/liquidity/useLiquidityPositions';
import { useLiquidityManagement, RemoveLiquidityParams } from '@/hooks/liquidity/useLiquidityManagement';
import { formatNumber } from '@/util/balance/formatNumber';
import { useNativeTokenHandler } from '@/hooks/liquidity/useNativeTokenHandler';
import { ChainType } from '@/constants/chain-specific';
interface RemoveLiquidityModalProps {
  position: LiquidityPosition;
  chainId?: ChainType;
  isOpen?: boolean;
  onClose?: () => void;
  onOpenChange?: (isOpen: boolean) => void;
  onSuccess: () => void;
}
export function RemoveLiquidityModal({
  position,
  chainId = 109,
  isOpen,
  onClose,
  onOpenChange,
  onSuccess
}: RemoveLiquidityModalProps) {
  const {
    removeLiquidity,
    state
  } = useLiquidityManagement(chainId);
  const {
    isWrappedNative,
    chainConfig
  } = useNativeTokenHandler(chainId);
  const [percentage, setPercentage] = useState(25);
  const [slippageTolerance, setSlippageTolerance] = useState(0.5);
  const [customSlippage, setCustomSlippage] = useState(false);
  const [collectFees, setCollectFees] = useState(true);

  // Detect wrapped native tokens in the position
  const wrappedNativeInfo = useMemo(() => {
    const token0IsWrapped = isWrappedNative(position.token0, position.dex);
    const token1IsWrapped = isWrappedNative(position.token1, position.dex);
    const hasWrappedNative = token0IsWrapped || token1IsWrapped;
    const wrappedToken = token0IsWrapped ? position.token0 : token1IsWrapped ? position.token1 : null;
    const nativeSymbol = chainConfig?.nativeCurrency?.symbol || 'ETH';
    return {
      hasWrappedNative,
      token0IsWrapped,
      token1IsWrapped,
      wrappedToken,
      nativeSymbol
    };
  }, [position, isWrappedNative, chainConfig]);

  // Default to receiving native when wrapped native is detected
  const [receiveNative, setReceiveNative] = useState(wrappedNativeInfo.hasWrappedNative);

  // Calculate amounts to receive
  const token0Amount = parseFloat(position.token0AmountFormatted) * percentage / 100;
  const token1Amount = parseFloat(position.token1AmountFormatted) * percentage / 100;
  const valueToReceive = position.totalValueUsd * percentage / 100;
  const feesToCollect = collectFees ? position.feesValueUsd : 0;
  const presetPercentages = [25, 50, 75, 100];
  const handleRemove = async () => {
    const params: RemoveLiquidityParams = {
      position,
      percentage,
      slippageTolerance,
      deadline: Math.floor(Date.now() / 1000) + 1200,
      // 20 minutes
      receiveNative: wrappedNativeInfo.hasWrappedNative ? receiveNative : false
    };
    try {
      await removeLiquidity(params);
      onSuccess();
      onClose?.();
    } catch (error) {
      console.error('Remove liquidity failed:', error);
    }
  };
  return <div className="space-y-6">
      {/* Position Info */}
      <Card>
        <CardBody>
          <div className="flex items-center gap-4">
            <div className="flex items-center -space-x-2">
              <Avatar src={position.token0.logo} size="md" className="border-2 border-background" />
              <Avatar src={position.token1.logo} size="md" className="border-2 border-background" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold">
                {position.token0.symbol}/{position.token1.symbol}
              </h3>
              <p className="text-sm text-default-500">
                {position.dexName} • {position.version.toUpperCase()}
              </p>
            </div>
            <div className="text-right">
              <div className="text-lg font-semibold">
                ${formatNumber(position.totalValueUsd, 2)}
              </div>
              <div className="text-xs text-default-500">Total Value</div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Amount Selection */}
      <Card>
        <CardBody className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold">Amount to Remove</h4>
            <span className="text-2xl font-bold text-primary">
              {percentage}%
            </span>
          </div>

          <div className="space-y-4">
            <Slider size="lg" step={1} showTooltip tooltipProps={{
            className: "text-tiny text-default-500 rounded-md",
            content: "Press Enter to confirm"
          }} minValue={1} maxValue={100} value={percentage} onChange={value => setPercentage(value as number)} className="max-w-full" marks={[{
            value: 0,
            label: '0%'
          }, {
            value: 25,
            label: '25%'
          }, {
            value: 50,
            label: '50%'
          }, {
            value: 75,
            label: '75%'
          }, {
            value: 100,
            label: '100%'
          }]} />

            <div className="flex gap-2">
              {presetPercentages.map(preset => <Button key={preset} size="sm" variant={percentage === preset ? 'solid' : 'flat'} color="primary" onPress={() => setPercentage(preset)} className="flex-1">
                  {preset}%
                </Button>)}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Slippage Settings */}
      <Card>
        <CardBody className="space-y-4">
          <h4 className="font-semibold">Slippage Tolerance</h4>
          
          <div className="flex items-center gap-2">
            <Switch size="sm" isSelected={customSlippage} onValueChange={setCustomSlippage} />
            <span className="text-sm">Custom slippage</span>
          </div>

          {customSlippage ? <Input type="number" label="Slippage (%)" value={slippageTolerance.toString()} onValueChange={value_0 => setSlippageTolerance(parseFloat(value_0) || 0.5)} min={0.1} max={50} step={0.1} endContent="%" /> : <Slider label="Slippage Tolerance" step={0.1} minValue={0.1} maxValue={5} value={slippageTolerance} onChange={value_1 => setSlippageTolerance(value_1 as number)} className="max-w-md" formatOptions={{
          style: 'percent',
          minimumFractionDigits: 1
        }} />}
        </CardBody>
      </Card>

      {/* Fee Collection */}
      {position.feesValueUsd > 0 && <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-semibold">Collect Accumulated Fees</h4>
                <p className="text-sm text-default-500">
                  ${formatNumber(position.feesValueUsd, 2)} in unclaimed fees
                </p>
              </div>
              <Switch isSelected={collectFees} onValueChange={setCollectFees} color="success" />
            </div>
          </CardBody>
        </Card>}

      {/* Summary */}
      <Card>
        <CardBody className="space-y-3">
          <h4 className="font-semibold">You Will Receive</h4>

          <div className="space-y-2">
            {/* Token 0 */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Avatar src={position.token0.logo} size="sm" />
                <span>
                  {wrappedNativeInfo.token0IsWrapped && receiveNative ? wrappedNativeInfo.nativeSymbol : position.token0.symbol}
                </span>
                {/* Toggle for wrapped native token0 */}
                {wrappedNativeInfo.token0IsWrapped && <Tooltip content={receiveNative ? `Receive as ${wrappedNativeInfo.nativeSymbol} (native)` : `Receive as ${position.token0.symbol} (wrapped)`}>
                    <button type="button" onClick={() => setReceiveNative(!receiveNative)} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-primary/10 hover:bg-primary/20 transition-colors">
                      <Icon icon={receiveNative ? "mdi:swap-horizontal" : "mdi:swap-horizontal"} className="w-3 h-3" />
                      {receiveNative ? wrappedNativeInfo.nativeSymbol : position.token0.symbol}
                    </button>
                  </Tooltip>}
              </div>
              <span className="font-medium">
                {formatNumber(token0Amount, 6)}
              </span>
            </div>

            {/* Token 1 */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Avatar src={position.token1.logo} size="sm" />
                <span>
                  {wrappedNativeInfo.token1IsWrapped && receiveNative ? wrappedNativeInfo.nativeSymbol : position.token1.symbol}
                </span>
                {/* Toggle for wrapped native token1 */}
                {wrappedNativeInfo.token1IsWrapped && <Tooltip content={receiveNative ? `Receive as ${wrappedNativeInfo.nativeSymbol} (native)` : `Receive as ${position.token1.symbol} (wrapped)`}>
                    <button type="button" onClick={() => setReceiveNative(!receiveNative)} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-primary/10 hover:bg-primary/20 transition-colors">
                      <Icon icon={receiveNative ? "mdi:swap-horizontal" : "mdi:swap-horizontal"} className="w-3 h-3" />
                      {receiveNative ? wrappedNativeInfo.nativeSymbol : position.token1.symbol}
                    </button>
                  </Tooltip>}
              </div>
              <span className="font-medium">
                {formatNumber(token1Amount, 6)}
              </span>
            </div>
          </div>

          <Divider />

          <div className="flex items-center justify-between">
            <span className="text-sm text-default-500">Total Value</span>
            <span className="font-semibold">
              ${formatNumber(valueToReceive + feesToCollect, 2)}
            </span>
          </div>

          {feesToCollect > 0 && <div className="flex items-center justify-between">
              <span className="text-sm text-success">+ Fees</span>
              <span className="font-medium text-success">
                ${formatNumber(feesToCollect, 2)}
              </span>
            </div>}
        </CardBody>
      </Card>

      {/* Warning for full removal */}
      {percentage === 100 && <Alert color="warning" variant="flat" title="Complete Position Removal" description="This will remove your entire liquidity position and close it permanently." />}

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button variant="flat" onPress={onClose} className="flex-1" isDisabled={state.isLoading}>
          Cancel
        </Button>
        <Button color="primary" onPress={handleRemove} className="flex-1" isLoading={state.isLoading}>
          {state.isLoading ? <div className="flex items-center gap-2">
              {state.step === 'approval' && 'Approving...'}
              {state.step === 'transaction' && 'Removing...'}
              {state.step === 'confirmed' && 'Confirmed!'}
            </div> : `Remove ${percentage}% Liquidity`}
        </Button>
      </div>

      {state.error && <Alert color="danger" variant="flat" title="Transaction Failed" description={state.error} />}
    </div>;
}